/**
 * ProjectGrid - Displays the grid of project cards with filtering
 */

import { useNavigate } from 'react-router-dom';
import { FolderGit2, Edit2, Trash2, MoreVertical, ArrowRight, RefreshCw } from 'lucide-react';
import {  ProjectStatuses } from '@resolve-ai/shared';
import type { ProjectListItem } from '@/lib/projects-api.ts';
import {GitHubLogo, JiraIcon} from '../icons';
import { STATUS_STYLES } from '@/types/consts';
import { useAuth } from '../../contexts/AuthContext';

interface ProjectGridProps {
  projects: ProjectListItem[];
  totalCount: number;
  statusFilter: 'all' | 'active' | 'inactive' | 'archived';
  canCreateProject: boolean;
  isLoadingIntegrations: boolean;
  isLoadingRepos: boolean;
  openMenuId: string | null;
  onToggleMenu: (id: string) => void;
  onEditClick: (project: ProjectListItem) => void;
  onDeleteClick: (project: ProjectListItem) => void;
  onOpenCreateModal: () => void;
}

export function ProjectGrid({
  projects,
  totalCount,
  statusFilter,
  canCreateProject,
  isLoadingIntegrations,
  isLoadingRepos,
  openMenuId,
  onToggleMenu,
  onEditClick,
  onDeleteClick,
  onOpenCreateModal,
}: ProjectGridProps) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  if (projects.length === 0 && !isLoadingIntegrations && !isLoadingRepos) {
    return (
      <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-12 text-center">
        <FolderGit2 className="w-12 h-12 text-gray-400 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
          {totalCount === 0
            ? 'No projects yet'
            : statusFilter === 'all'
              ? 'No projects'
              : `No ${statusFilter} projects`
          }
        </h3>
        <p className="text-gray-600 dark:text-gray-400 mb-4">
          {totalCount === 0
            ? 'Create your first project to start automating bug resolution'
            : statusFilter === 'all'
              ? 'No projects found'
              : `You don't have any ${statusFilter} projects`
          }
        </p>
        {/* Only show Create Project button for 'all' and 'active' filters */}
        {(statusFilter === 'all' || statusFilter === 'active') && (
          <>
            <button
              onClick={onOpenCreateModal}
              disabled={!canCreateProject || isLoadingIntegrations || isLoadingRepos}
              className={`px-4 py-2 rounded-xl transition-colors ${
                canCreateProject && !isLoadingIntegrations
                  ? 'bg-indigo-600 hover:bg-indigo-700 text-white'
                  : 'bg-gray-300 dark:bg-gray-600 text-gray-500 dark:text-gray-400 cursor-not-allowed'
              }`}
              title={!canCreateProject ? 'Connect GitHub and Jira in Settings first' : undefined}
            >
              {isLoadingRepos ? (
                <RefreshCw className="w-4 h-4 animate-spin inline mr-2" />
              ) : null}
              Create Project
            </button>
            {!isLoadingIntegrations && !canCreateProject && (
              <p className="text-sm text-amber-600 dark:text-amber-400 mt-4">
                {isAdmin ? (
                  <>
                    <a href="/settings" className="underline hover:no-underline">
                      Connect GitHub and Jira
                    </a>{' '}
                    to create projects
                  </>
                ) : (
                  'Contact an admin to connect GitHub and Jira before creating projects'
                )}
              </p>
            )}
          </>
        )}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {projects.map((project) => (
        <div
          key={project.id}
          onClick={() => navigate(`/projects/${project.id}`)}
          className={`
            group relative bg-white dark:bg-gray-800
            transition-all duration-200 cursor-pointer
            hover:shadow-lg hover:-translate-y-0.5 project-card
          `}
        >
          <div className="">
            {/* Header: Icon + Name + Menu */}
            <div className="flex items-start justify-between gap-3 mb-3">
              <div className="flex items-center gap-3 min-w-0">
                {/* Status-aware icon */}
                <div className={`
                  shrink-0 w-22 h-22 rounded-xl flex items-center justify-center
                  ${project.status === ProjectStatuses.ACTIVE
                    ? 'bg-linear-to-br from-indigo-500 to-purple-600 shadow-lg shadow-indigo-500/25'
                    : 'bg-gray-100 dark:bg-gray-700'
                  }
                `}>
                  <FolderGit2 className={`w-10 h-10 ${project.status === ProjectStatuses.ACTIVE ? 'text-white' : 'text-gray-400'}`} />
                </div>
              </div>

              {/* Overflow menu - hover only */}
              <div className="relative shrink-0" onClick={(e) => e.stopPropagation()}>
                <button
                  onClick={() => onToggleMenu(project.id)}
                  className="p-1.5 rounded-lg
                             text-gray-400 hover:text-gray-600 dark:hover:text-gray-300
                             hover:bg-gray-100 dark:hover:bg-gray-700 transition-all"
                >
                  <MoreVertical className="w-6 h-6" />
                </button>

                {/* Dropdown menu */}
                {openMenuId === project.id && (
                  <div className="absolute right-0 top-8 z-10 w-36 py-1
                                  bg-white dark:bg-gray-800 rounded-xl shadow-lg
                                  border border-gray-200 dark:border-gray-700">
                    <button
                      onClick={() => onEditClick(project)}
                      className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 dark:text-gray-300
                                 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                    >
                      <Edit2 className="w-4 h-4" /> Edit
                    </button>
                    <button
                      onClick={() => onDeleteClick(project)}
                      className="w-full flex items-center gap-2 px-4 py-2 text-sm text-red-600 dark:text-red-400
                                 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                    >
                      <Trash2 className="w-4 h-4" /> Delete
                    </button>
                  </div>
                )}
              </div>
            </div>
            <div className='mt-6 flex items-center gap-2'>
               <span data-size={"md"} className={`
                      circle w-2.5 h-2.5
                       ${STATUS_STYLES[project.status] ?? ""}
                    `} />
              <span className="font-subheading text-gray-900 dark:text-white capitalize ">{project.name || ''}</span>
            </div>
            {/* Description */}
            <p className="font-body-sm mt-2 text-gray-500 dark:text-white-500 line-clamp-3 mb-4 min-h-15">
              {project.description || 'No description provided'}
            </p>

            {/* Footer metadata */}
            <div className="flex items-center gap-3 mt-4">
              <div className="flex items-center gap-[0.38rem] font-body-sm text-gray-500 dark:text-white-500">
                <GitHubLogo className="w-4.5 h-4.5" />
                <span>{`${project.githubRepoCount} Repositories`}</span>
              </div>
              <div data-size={'sm'} className={'circle bg-gray-500 dark:bg-white-500'}/>
              {project.jiraProjectKey && (
                <div className="flex items-center gap-[0.38rem] text-xs text-gray-500 dark:text-white-500">
                  <JiraIcon className="w-4.5 h-4.5 text-blue-600" />
                  <span>{project.jiraProjectKey}</span>
                </div>
              )}
              <div className="flex-1" />
              <ArrowRight className="w-4 h-4 text-gray-300 dark:text-gray-600
                                      opacity-0 group-hover:opacity-100
                                      group-hover:translate-x-1 transition-all" />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
